import { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Select } from '../ui/select';

const API_BASE = process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3010/api';
const MONTHS = ['', 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const Export = ({ user, token }) => {
  const [format, setFormat] = useState('xlsx');
  const [range, setRange] = useState('all');
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  const buildFilename = () => {
    const ext = format === 'xlsx' ? 'xlsx' : 'csv';
    if (range === 'month') return `milewise_${year}-${String(month).padStart(2, '0')}.${ext}`;
    if (range === 'ytd') return `milewise_ytd-${currentYear}.${ext}`;
    return `milewise_all.${ext}`;
  };

  const handleExport = async () => {
    setBusy(true);
    setError('');
    setStatus('');
    try {
      const params = new URLSearchParams({ format, range });
      if (range === 'month') {
        params.set('year', year);
        params.set('month', month);
      }
      const res = await fetch(`${API_BASE}/trips/export?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${res.status}`);
      }
      const refreshed = res.headers.get('X-Refreshed-Token');
      if (refreshed) localStorage.setItem('milewise_token', refreshed);

      const blob = await res.blob();
      const filename = buildFilename();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setStatus(`Downloaded ${filename}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-4">
      <Card>
        <CardHeader className="py-3">
          <CardTitle className="text-base">Export Data</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3 flex-wrap">
            <Label className="w-28">Format</Label>
            <Select value={format} onChange={e => setFormat(e.target.value)} className="w-48">
              <option value="xlsx">Excel (.xlsx)</option>
              <option value="csv">CSV (.csv)</option>
            </Select>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <Label className="w-28">Range</Label>
            <Select value={range} onChange={e => setRange(e.target.value)} className="w-48">
              <option value="all">All data</option>
              <option value="ytd">Year to date</option>
              <option value="month">Specific month</option>
            </Select>
          </div>

          {range === 'month' && (
            <div className="flex items-center gap-3">
              <Label className="w-28">Month</Label>
              <Select value={year} onChange={e => setYear(e.target.value)} className="w-28">
                {years.map(y => <option key={y} value={y}>{y}</option>)}
              </Select>
              <Select value={month} onChange={e => setMonth(e.target.value)} className="w-40">
                {MONTHS.map((m, i) => i > 0 && <option key={i} value={i}>{m}</option>)}
              </Select>
            </div>
          )}

          <div className="pt-2">
            <Button onClick={handleExport} disabled={busy}>
              {busy ? 'Exporting...' : 'Download'}
            </Button>
          </div>

          {status && <div className="text-sm text-emerald-500">{status}</div>}
          {error && <div className="text-sm text-destructive">Error: {error}</div>}

          <div className="text-xs text-muted-foreground pt-2 border-t">
            Exports itemized trip rows (date, customer, billable/unbillable hours, mileage, cost, description) with a totals row.
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Export;
